import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { Product } from '../../order/models/product';
import { OrderTypeEnum } from '../../order/models/order-type.enum';
import { OrderStateService } from '../../order/services/order-state.service';
import { ProductsStateService } from '../../order/services/products-state.service';
import { LoggerService } from '../../../services/logger/logger.service';
import { InactiveTimerService } from '../../../services/inactive-timer.service';
import { ShopSettingsService } from '../../general/services/shop-settings.service';
import { PartnerStateService } from '../../partner/services/partner-state.service';
import { PartnerCodeStateService } from '../../partner-code/services/partner-code-state.service';
import { catchError, map, switchMap } from 'rxjs/operators';
import { BaseStepService } from '../../general/services/base-step.services';
import { BookingProductStepEnum } from '../models/booking-product-step.enum';
import { Category } from '../../order/models/category';
import { BookingOrderService } from './booking-order.service';


@Injectable()
export class BookingProductStepService extends BaseStepService<BookingProductStepEnum> {
  private _products: Product[];
  private _categories: Category[];

  constructor(
    protected logger: LoggerService,
    protected inactiveTimerService: InactiveTimerService,
    protected shopSettingsService: ShopSettingsService,
    protected partnerStateService: PartnerStateService,
    protected partnerCodeStateService: PartnerCodeStateService,
    private router: Router,
    private orderStateService: OrderStateService,
    private productsStateService: ProductsStateService,
    private bookingOrderService: BookingOrderService,
  ) {
    super(
      logger,
      inactiveTimerService,
      shopSettingsService,
      partnerStateService,
      partnerCodeStateService
    );
    this._steps = [
      BookingProductStepEnum.Init,
      BookingProductStepEnum.ProductSelection,
      BookingProductStepEnum.ShowProductConfigurationPage,
      BookingProductStepEnum.Authentication,
      BookingProductStepEnum.Payment,
    ];
  }

  protected get serviceName(): string {
    return BookingProductStepService.name;
  }

  protected get initStep(): BookingProductStepEnum {
    return BookingProductStepEnum.Init;
  }

  protected get resetStep(): BookingProductStepEnum {
    return BookingProductStepEnum.Init;
  }

  get products(): Product[] {
    return this._products;
  }

  get categories(): Category[] {
    return this._categories;
  }

  get existProducts(): boolean {
    return (this._products != null && this._products.length > 0);
  }

  get isPaymentPageAvailable(): boolean {

    const order = this.orderStateService.order;

    if (!order || order.orderType !== OrderTypeEnum.Product) {
      return false;
    }
    return order.orderLines.length > 0;
  }

  private _showProductAuthenticationComponent(): void {
    this.router.navigate(['booking/products/authentication']);
  }

  private _showProductOrderComponent(): void {
    this.router.navigate(['booking/products/select-products']);
  }

  private _showProductPaymentComponent(): void {
    this.router.navigate(['payment/start']);
  }

  initialize(): Observable<boolean> {
    return of(true).pipe(
      switchMap(_ => this.orderStateService.initialize(OrderTypeEnum.Product)),
      switchMap(_ => super.initialize()),
    );
  }

  protected _clearCache(): void {
    this._products = null;
    this._categories = null;
  }

  protected _fillCache(): Observable<boolean> {
    return of(true).pipe(
      switchMap(_ => {
        if (this._products && !this.productsStateService.isHaveStockProduct) {
          return of(_);
        }
        return this.productsStateService.products.pipe(
          map(allProducts => {
            this.logger.info(`${this.serviceName}: initialize products received.`);
            this._fillProducts(allProducts);
            return _;
          }),
          catchError(error => {
            this.logger.info(`${this.serviceName}: initialize products receive error ${error}.`);
            return throwError(error);
          }),
        );
      }),
      switchMap(_ => {
        if (this._categories) {
          return of(_);
        }
        return this.productsStateService.categories.pipe(
          map(categories => {
            this.logger.info(`${this.serviceName}: initialize categories received.`);
            this._categories = categories
            return _;
          }),
          catchError(error => {
            this.logger.info(`${this.serviceName}: initialize categories   receive error ${error}.`);
            return throwError(error);
          }),
        );
      }),
    );
  }

  private _fillProducts(allProducts: Product[]): void {
    this._products = allProducts;
  }

  isAvailableStep(step: BookingProductStepEnum): boolean {
    if (!step) {
      return false;
    }

    switch (step) {
      case BookingProductStepEnum.ShowProductConfigurationPage:
        return this.isShowProductConfigurationPage();
      case BookingProductStepEnum.Authentication:
        return this.needAuth;
      default:
        return true;
    }
  }

  protected get prevStepForIndexMinusOne(): BookingProductStepEnum {
    return BookingProductStepEnum.Init;
  }

  update(
    step: BookingProductStepEnum = BookingProductStepEnum.Init,
  ): void {

    super.update(step);

    switch (this._step) {
      case BookingProductStepEnum.ProductSelection:
        this._showProductOrderComponent();
        break;
      case BookingProductStepEnum.ShowProductConfigurationPage:
        this._showProductConfigurationPage();
        break;
      case BookingProductStepEnum.Authentication:
        this._showProductAuthenticationComponent();
        break;
      case BookingProductStepEnum.Payment:
        this._showProductPaymentComponent();
        break;
      case BookingProductStepEnum.Init:
      default:
        this.initialize().subscribe();
        break;
    }
  }

  private _showProductConfigurationPage(): void {
    this.router.navigateByUrl('/booking/products/configuration');
  }

  isShowProductConfigurationPage(): boolean {
    let isShowPage = false;
    const orderLines = this.bookingOrderService.order.orderLines;
    const products = this.products;
    if ((!orderLines || orderLines.length < 1) || !this.existProducts) {
      return isShowPage;
    }

    for (const orderLine of orderLines) {
      isShowPage = products.some(item =>
        (item.id === orderLine.productId) &&
        (item.isBirthdayRequired || item.isPartnerRequired || item.isAddressRequired || item.isPhoneRequired || item.isEmailRequired));
      if (isShowPage) {
        break;
      }
    }

    return isShowPage;
  }

  get isBookingOrderAvailable(): boolean {
    switch (this.step) {
      case BookingProductStepEnum.Init:
      case BookingProductStepEnum.Payment:
      case BookingProductStepEnum.ExtraProductSelection:
        return false;
    }

    return true;
  }

  get isBookingProductConfigurationAvailable(): boolean {
    switch (this.step) {
      case BookingProductStepEnum.Init:
      case BookingProductStepEnum.ProductSelection:
        return false;
    }

    return true;    
  }

  get isBookingProductAuthAvailable(): boolean {
    switch (this.step) {
      case BookingProductStepEnum.Init:
      case BookingProductStepEnum.ProductSelection:
      case BookingProductStepEnum.ShowProductConfigurationPage:
        return false;
    }

    return true;
  }
}
